/**
 * PlanTimeline — sheet showing all tasks in a Smart Plan with timeline,
 * progress, reschedule and delete actions.
 *
 * Opened by clicking PlanBadge on any task belonging to a plan.
 */

import { memo, useState, useCallback, useMemo, useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import dayjs from "dayjs"
import { cn } from "@/shared/utils/general/cn"
import { toUtcMidnight } from "@/shared/utils/date/dateUtils"
import { db } from "@/db/schema"
import type { DbObject } from "@/db/schema"
import { useLiveQueryImmediate } from "@/db/hooks"
import {
  getPlanTasks,
  reschedulePlan,
  deletePlan,
  completeTask,
  uncompleteTask,
} from "@/db/operations"
import type { DbPlanInstance, DbPack, DbStartAtIndex, PlanStepInstance } from "@totodo/operations"
import { ObjectStatus, parseDbDate } from "@totodo/operations"
import { TaskItem } from "@/features/tasks/components/TaskItem"
import { useRevealTask } from "@/features/search/useRevealTask"
import { useViewContext } from "@/app/stores/viewContext"
import { ResponsiveSheet } from "@/app/components/ui/responsive-sheet"
import { ContainerIcon } from "@/shared/icons/resolveContainerIcon"
import { MiniCalendar } from "@/features/schedule/MiniCalendar"
import PlansListIcon from "@/shared/icons/totodoicons/stroke/plans-list.svg?react"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import ChevronLeftIcon from "@/shared/icons/totodoicons/stroke/chevron-left.svg?react"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PlanTimelineProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The anchor task ID for this plan */
  anchorTaskId: string
  /** The plan instance ID */
  planId: string
}

type View = "timeline" | "reschedule" | "confirm-delete"

interface MonthGroup {
  label: string
  tasks: TimelineTask[]
}

interface TimelineTask {
  task: DbObject
  startAt?: string // ms timestamp as string from DbStartAtIndex
  stepInfo?: PlanStepInstance
  isAnchor: boolean
  isOverdue: boolean
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const PlanTimeline = memo(function PlanTimeline({
  open,
  onOpenChange,
  anchorTaskId,
  planId,
}: PlanTimelineProps) {
  const { t } = useTranslation()
  const [view, setView] = useState<View>("timeline")
  const [newAnchorDate, setNewAnchorDate] = useState<Date>(new Date())
  const [rescheduling, setRescheduling] = useState(false)
  const [deleting, setDeleting] = useState(false)

  // Fetch anchor task
  const [anchorTask] = useLiveQueryImmediate(
    () => db.totodo.get(anchorTaskId),
    ["plan-timeline-anchor", anchorTaskId],
    undefined,
  )

  // Fetch plan instance
  const [planInstance] = useLiveQueryImmediate(
    () => db.plan.get(planId) as Promise<DbPlanInstance | undefined>,
    ["plan-timeline-instance", planId],
    undefined,
  )

  // Fetch pack for name/icon
  const [pack] = useLiveQueryImmediate(
    () => {
      if (!planInstance?.packId) return undefined
      return db.plan.get(planInstance.packId) as Promise<DbPack | undefined>
    },
    ["plan-timeline-pack", planInstance?.packId],
    undefined,
  )

  // Fetch all plan tasks (live)
  const [planTasks] = useLiveQueryImmediate(
    () => getPlanTasks(anchorTaskId),
    ["plan-timeline-tasks", anchorTaskId],
    [] as DbObject[],
  )

  // Fetch startAt dates for all tasks (including anchor)
  const [dateMap] = useLiveQueryImmediate(
    async () => {
      const allIds = planTasks.map((t) => `#${t.id}`)
      if (anchorTask?.id) allIds.push(`#${anchorTask.id}`)
      if (allIds.length === 0) return new Map<string, DbStartAtIndex>()
      const entries = await db.startAtIndex.bulkGet(allIds)
      const map = new Map<string, DbStartAtIndex>()
      for (const entry of entries) {
        if (entry?.id) map.set(entry.id, entry)
      }
      return map
    },
    ["plan-timeline-dates", planTasks.length, anchorTask?.id],
    new Map<string, DbStartAtIndex>(),
  )

  // Build step lookup
  const stepByTaskId = useMemo(() => {
    if (!planInstance?.steps) return new Map<string, PlanStepInstance>()
    return new Map(planInstance.steps.map((s) => [s.taskId, s]))
  }, [planInstance?.steps])

  // Progress
  const progress = useMemo(() => {
    const total = planTasks.length
    const completed = planTasks.filter(
      (t) => t.status === ObjectStatus.Completed,
    ).length
    return {
      total,
      completed,
      percent: total ? Math.round((completed / total) * 100) : 0,
    }
  }, [planTasks])

  // Group tasks by month
  const monthGroups = useMemo((): MonthGroup[] => {
    const now = dayjs()

    const allTasks: TimelineTask[] = planTasks.map((task) => {
      const dateEntry = dateMap.get(`#${task.id}`)
      const startAt = dateEntry?.startAt
      const startMs = parseDbDate(startAt)
      return {
        task,
        startAt,
        stepInfo: stepByTaskId.get(task.id!),
        isAnchor: false,
        isOverdue:
          startMs !== undefined &&
          startMs < now.valueOf() &&
          task.status !== ObjectStatus.Completed,
      }
    })

    // Add anchor task at its date position
    if (anchorTask && planInstance) {
      const anchorDateEntry = dateMap.get(`#${anchorTask.id}`)
      allTasks.push({
        task: anchorTask,
        startAt: anchorDateEntry?.startAt,
        stepInfo: undefined,
        isAnchor: true,
        isOverdue: false,
      })
    }

    // Sort by startAt date
    allTasks.sort((a, b) => {
      const aDate = parseDbDate(a.startAt) ?? 0
      const bDate = parseDbDate(b.startAt) ?? 0
      if (aDate !== bDate) return aDate - bDate
      // Anchor at end of its date group
      if (a.isAnchor) return 1
      if (b.isAnchor) return -1
      return (a.task.anchorTaskIndex ?? 0) - (b.task.anchorTaskIndex ?? 0)
    })

    const groups = new Map<string, TimelineTask[]>()
    for (const item of allTasks) {
      const itemMs = parseDbDate(item.startAt)
      const d = itemMs !== undefined ? dayjs(itemMs) : dayjs()
      const key = d.format("YYYY-MM")
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(item)
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, tasks]) => ({
        label: dayjs(parseDbDate(tasks[0]?.startAt) ?? undefined).format("MMMM YYYY"),
        tasks,
      }))
  }, [planTasks, anchorTask, planInstance, stepByTaskId, dateMap])

  // Task action handlers
  const handleComplete = useCallback((id: string) => {
    completeTask(id)
  }, [])

  const handleUncomplete = useCallback((id: string) => {
    uncompleteTask(id)
  }, [])

  // Override visibleTaskIds with plan order for correct shift-select
  const savedIdsRef = useRef<string[] | null>(null)
  const planTaskIds = useMemo(
    () => monthGroups.flatMap((g) => g.tasks.map((t) => t.task.id!)),
    [monthGroups],
  )
  useEffect(() => {
    if (!open || planTaskIds.length === 0) return
    const store = useViewContext.getState()
    // Save current order on first open
    if (!savedIdsRef.current) {
      savedIdsRef.current = store.visibleTaskIds
    }
    store.setVisibleTaskIds(planTaskIds)
    return () => {
      // Restore on unmount/close
      if (savedIdsRef.current) {
        useViewContext.getState().setVisibleTaskIds(savedIdsRef.current)
        savedIdsRef.current = null
      }
    }
  }, [open, planTaskIds])

  // Reset on close
  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        setTimeout(() => {
          setView("timeline")
          setRescheduling(false)
          setDeleting(false)
        }, 300)
      }
      onOpenChange(open)
    },
    [onOpenChange],
  )

  // Reschedule handler
  const handleReschedule = useCallback(async () => {
    setRescheduling(true)
    const result = await reschedulePlan(
      anchorTaskId,
      toUtcMidnight(newAnchorDate),
    )
    setRescheduling(false)
    if (result.ok) {
      setView("timeline")
    }
  }, [anchorTaskId, newAnchorDate])

  // Delete handler
  const handleDelete = useCallback(async () => {
    setDeleting(true)
    const result = await deletePlan(anchorTaskId)
    setDeleting(false)
    if (result.ok) {
      onOpenChange(false)
    }
  }, [anchorTaskId, onOpenChange])

  // Double-click task → close sheet, navigate to task in its container
  const revealTask = useRevealTask()
  const handleTaskOpen = useCallback(
    (taskId: string) => {
      onOpenChange(false)
      // Small delay so sheet animates closed before navigation
      setTimeout(() => revealTask(taskId), 150)
    },
    [onOpenChange, revealTask],
  )

  const planName = pack?.name ?? t("Smart Plan")
  const anchorDateMs = planInstance?.anchorDate
    ? Number(planInstance.anchorDate)
    : undefined

  return (
    <ResponsiveSheet
      open={open}
      onOpenChange={handleOpenChange}
      title={planName}
      className="rounded-t-2xl"
    >
      <div className="flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b px-4 py-3">
          {view !== "timeline" ? (
            <button
              onClick={() => setView("timeline")}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ChevronLeftIcon className="size-4" />
            </button>
          ) : (
            <div className="size-4" />
          )}
          <h3 className="text-base font-semibold">
            {view === "timeline"
              ? planName
              : view === "reschedule"
                ? t("Reschedule plan")
                : t("Delete plan")}
          </h3>
          <button
            onClick={() => onOpenChange(false)}
            className="text-muted-foreground hover:text-foreground"
          >
            <CrossIcon className="size-4" />
          </button>
        </div>

        {/* Timeline View */}
        {view === "timeline" && (
          <div className="flex-1 overflow-y-auto">
            {/* Plan summary */}
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-muted-foreground">
              {pack?.icon ? (
                <ContainerIcon icon={pack.icon} size="1rem" />
              ) : (
                <PlansListIcon className="size-4" />
              )}
              <span>
                {progress.completed}/{progress.total} {t("tasks completed")}
              </span>
              {anchorDateMs && (
                <>
                  <span className="text-muted-foreground/40">·</span>
                  <span>{dayjs(anchorDateMs).format("D MMM YYYY")}</span>
                </>
              )}
            </div>

            {/* Progress bar */}
            <div className="mx-4 mb-3 h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-all duration-300"
                style={{ width: `${progress.percent}%` }}
              />
            </div>

            {/* Month groups — timeline with TaskItem */}
            <div className="pb-4">
              {monthGroups.map((group, gi) => (
                <div key={group.label}>
                  {/* Month label — with continuous timeline line */}
                  <div className={cn(
                    "relative pl-8 pr-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground",
                    "before:absolute before:left-[17.5px] before:-bottom-0.5 before:w-px before:bg-muted-foreground/25 before:pointer-events-none",
                    gi === 0
                      ? "mb-0.5 before:top-0"
                      : "mt-2 mb-0.5 before:-top-2",
                  )}>
                    {group.label}
                  </div>
                  <div>
                    {group.tasks.map((item, idx) => (
                      <div
                        key={item.task.id}
                        className="plan-timeline-row"
                      >
                        <TaskItem
                          task={item.task}
                          startAtData={dateMap.get(`#${item.task.id}`)}
                          onComplete={handleComplete}
                          onUncomplete={handleUncomplete}
                          onOpen={handleTaskOpen}
                          hideContainerLabel
                          showDateChip
                          isFirstInGroup={idx === 0}
                          isLastInGroup={idx === group.tasks.length - 1}
                          zoom={0.8}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Actions */}
            <div className="flex gap-2 border-t px-4 py-3">
              <button
                onClick={() => {
                  if (anchorDateMs) setNewAnchorDate(new Date(anchorDateMs))
                  setView("reschedule")
                }}
                className={cn(
                  "flex-1 rounded-lg border px-3 py-2 text-sm font-medium",
                  "text-foreground hover:bg-muted transition-colors",
                )}
              >
                {t("Reschedule")}
              </button>
              <button
                onClick={() => setView("confirm-delete")}
                className={cn(
                  "flex-1 rounded-lg border border-destructive/20 px-3 py-2 text-sm font-medium",
                  "text-destructive hover:bg-destructive/10 transition-colors",
                )}
              >
                {t("Delete plan")}
              </button>
            </div>
          </div>
        )}

        {/* Reschedule View */}
        {view === "reschedule" && (
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <p className="mb-4 text-sm text-muted-foreground">
              {t("Pick a new anchor date. All plan tasks will be rescheduled.")}
            </p>
            <MiniCalendar
              selectedDate={newAnchorDate}
              onSelect={setNewAnchorDate}
              minDate={new Date()}
            />
            <div className="mt-2 text-center text-sm text-muted-foreground">
              {progress.total} {t("tasks will be rescheduled")}
            </div>
            <button
              onClick={handleReschedule}
              disabled={rescheduling}
              className={cn(
                "mt-4 w-full rounded-xl py-3 text-sm font-semibold text-white",
                "bg-primary hover:bg-primary/90 disabled:opacity-50",
                "transition-colors",
              )}
            >
              {rescheduling
                ? t("Rescheduling...")
                : `${t("Reschedule")} — ${progress.total} ${t("tasks")}`}
            </button>
          </div>
        )}

        {/* Confirm Delete View */}
        {view === "confirm-delete" && (
          <div className="flex-1 px-4 py-6 text-center">
            <div className="mb-4 text-4xl">🗑️</div>
            <p className="mb-2 text-sm font-medium">
              {t("Delete this plan and all its tasks?")}
            </p>
            <p className="mb-6 text-xs text-muted-foreground">
              {progress.total} {t("tasks will be permanently deleted.")}
              <br />
              {t("The anchor task will be kept.")}
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => setView("timeline")}
                className={cn(
                  "flex-1 rounded-xl border py-3 text-sm font-medium",
                  "text-foreground hover:bg-muted transition-colors",
                )}
              >
                {t("Cancel")}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleting}
                className={cn(
                  "flex-1 rounded-xl py-3 text-sm font-semibold text-white",
                  "bg-destructive hover:bg-destructive/90 disabled:opacity-50",
                  "transition-colors",
                )}
              >
                {deleting ? t("Deleting...") : t("Delete")}
              </button>
            </div>
          </div>
        )}
      </div>
    </ResponsiveSheet>
  )
})
